import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import { App, UserRole, CandidateState, CandidateJobUtils, } from '../app';
import { AppEventType } from '../app/appEvents';
// Re-export enums and classes as values
export { CandidateState, UserRole, CandidateJobUtils };
const AppContext = createContext(undefined);
export const AppProvider = ({ children, role }) => {
    // State
    const [authError, setAuthError] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);
    // App instance - create once and always available (initializes sync)
    const [app] = useState(() => {
        return new App(role);
    });
    // Set up event listeners after all functions are defined
    useEffect(() => {
        const handleUserLoggedIn = () => {
            console.log('🔐 User logged in successfully');
            setAuthError(null);
        };
        const handleUserLoggedOut = () => {
            console.log('🔐 User logged out');
            setAuthError(null);
        };
        const handleError = (event) => {
            if (event.source === 'authStateChange' || event.source === 'login') {
                console.error('🔐 Authentication error:', event.error);
                const errorMessage = event.error instanceof Error ? event.error.message : 'Unknown error';
                // Check if this is an access denied error
                const isAccessDenied = errorMessage.includes('not found') ||
                    errorMessage.includes('USER_NOT_FOUND') ||
                    errorMessage.includes('access denied') ||
                    errorMessage.includes('unauthorized') ||
                    errorMessage.includes('ADMIN_REQUIRED') ||
                    errorMessage.includes('Admin access required');
                if (isAccessDenied) {
                    setAccessDenied(true);
                }
                else {
                    setAuthError(errorMessage);
                }
            }
        };
        // Set up event listeners
        app.on(AppEventType.USER_LOGGED_IN, handleUserLoggedIn);
        app.on(AppEventType.USER_LOGGED_OUT, handleUserLoggedOut);
        app.on(AppEventType.ERROR_OCCURRED, handleError);
        // Cleanup event listeners
        return () => {
            app.off(AppEventType.USER_LOGGED_IN, handleUserLoggedIn);
            app.off(AppEventType.USER_LOGGED_OUT, handleUserLoggedOut);
            app.off(AppEventType.ERROR_OCCURRED, handleError);
        };
    }, [app]);
    // Show authentication error
    if (authError) {
        return (_jsx("div", { style: {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f5f5f5',
                fontFamily: 'Arial, sans-serif',
                padding: '20px',
                textAlign: 'center'
            }, children: _jsxs("div", { style: {
                    maxWidth: '400px',
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }, children: [_jsx("h2", { style: { color: '#d32f2f', marginBottom: '20px' }, children: "Access Denied" }), _jsx("p", { style: { color: '#666', marginBottom: '20px', lineHeight: '1.5' }, children: authError }), _jsx("button", { onClick: () => window.location.reload(), style: {
                            padding: '10px 20px',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }, children: "Try Again" })] }) }));
    }
    const contextValue = {
        app,
        role,
        authError,
        accessDenied,
    };
    return (_jsx(AppContext.Provider, { value: contextValue, children: children }));
};
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};
